import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Site } from '../../../domain/Site';
import type { Device } from '../../../domain/Device';
import { FIELD_LAYERS, fieldGeometry, fieldRange, type FieldLayer, type FieldStation } from '../../../domain/FieldMap';

// Las estaciones publican cada pocos segundos; el mapa se refresca solo
// cada medio minuto mientras está abierto.
const FIELD_REFRESH_MS = 30_000;

export type MapMode = '3d' | '2d';

export function useSiteMapPage() {
  const history = useHistory();
  const { sites: siteRepo, devices: deviceRepo } = useRepositories();

  const [sites, setSites] = useState<Site[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // El spinner es solo para la primera carga: al volver a la pantalla (o en
  // cada refresco) los datos se actualizan sin mover la página.
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<MapMode>('3d');
  const [layer, setLayer] = useState<FieldLayer | null>(null);
  // Valor actual de cada estación del campo en todas las capas.
  const [field, setField] = useState<FieldStation[]>([]);
  const [visible, setVisible] = useState(false);

  const load = async () => {
    if (!loadedRef.current) setLoading(true);
    setError(null);
    try {
      const [{ sites: siteList }, { devices: deviceList }] = await Promise.all([siteRepo.list(), deviceRepo.list()]);
      setSites(siteList);
      setDevices(deviceList);
      // No pisa la selección del usuario al recargar la pestaña; solo elige
      // un campo por defecto la primera vez que hay datos.
      setSelectedSiteId((current) => current ?? siteList[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el mapa.');
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    setVisible(true);
    load();
  });

  useIonViewWillLeave(() => setVisible(false));

  const selectedSite = useMemo(() => sites.find((s) => s.id === selectedSiteId) ?? null, [sites, selectedSiteId]);

  const siteDevices = useMemo(
    () => devices.filter((d) => d.siteId === selectedSiteId && d.lat !== null && d.lng !== null),
    [devices, selectedSiteId]
  );

  // Datos del mapa: al cambiar de campo y después cada FIELD_REFRESH_MS
  // mientras la pantalla está abierta. Un refresco que falla deja los
  // valores anteriores en vez de vaciar el mapa.
  useEffect(() => {
    if (!selectedSiteId || !visible) return;
    let cancelled = false;
    const fetchField = () =>
      siteRepo
        .fetchField(selectedSiteId)
        .then((r) => {
          if (!cancelled) setField(r.stations);
        })
        .catch(() => undefined);
    fetchField();
    const timer = setInterval(fetchField, FIELD_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [siteRepo, selectedSiteId, visible]);

  // Al cambiar de campo no se muestran las estaciones del anterior.
  useEffect(() => setField([]), [selectedSiteId]);

  // Capas con al menos una estación con dato; si la elegida no tiene, se
  // pasa a la primera que sí.
  const availableLayers = useMemo(() => FIELD_LAYERS.filter((l) => field.some((st) => st.values[l] !== null)), [field]);
  const activeLayer: FieldLayer | null = layer && availableLayers.includes(layer) ? layer : availableLayers[0] ?? null;

  const fieldStations = useMemo(
    () => field.map((st) => ({ id: st.id, name: st.name, lat: st.lat, lng: st.lng, value: activeLayer ? st.values[activeLayer] : null })),
    [field, activeLayer]
  );

  // De qué valor a qué valor va la altura del volumen (para la leyenda).
  const layerRange = useMemo(
    () =>
      activeLayer
        ? fieldRange(
            activeLayer,
            fieldStations.map((st) => st.value).filter((v): v is number => v !== null)
          )
        : null,
    [activeLayer, fieldStations]
  );

  // Clave estable: la escena 3D solo se rearma si cambian las posiciones.
  const boundsKey = field.map((st) => `${st.lat},${st.lng}`).join(';');
  const bounds = useMemo(
    () => fieldGeometry(field.map((st) => ({ lat: st.lat, lng: st.lng }))),
    [boundsKey]
  );

  const devicesWithoutLocation = useMemo(
    () => devices.filter((d) => d.siteId === selectedSiteId && (d.lat === null || d.lng === null)).length,
    [devices, selectedSiteId]
  );

  return {
    sites,
    selectedSiteId,
    setSelectedSiteId,
    selectedSite,
    siteDevices,
    devicesWithoutLocation,
    mode,
    setMode,
    availableLayers,
    activeLayer,
    setLayer,
    fieldStations,
    layerRange,
    bounds,
    loading,
    error,
    goToDevice: (id: string) => history.push(`/app/devices/${id}`),
  };
}
